import { createEmptyRecord } from "./dexaRecord.js";
import { detectDexaPageRole } from "./dexaVisuals.js";

const NUMBER = "-?\\d+(?:[.,]\\d+)?";
const spanishMonths = {
  enero: 1,
  febrero: 2,
  marzo: 3,
  abril: 4,
  mayo: 5,
  junio: 6,
  julio: 7,
  agosto: 8,
  septiembre: 9,
  setiembre: 9,
  octubre: 10,
  noviembre: 11,
  diciembre: 12,
};

function numeric(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function cleanText(value) {
  return String(value ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[‐‑‒–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

function titleCase(value) {
  const source = cleanText(value).toLocaleLowerCase("es");
  return source.replace(/(^|[\s-])([\p{L}])/gu, (_, boundary, letter) => (
    `${boundary}${letter.toLocaleUpperCase("es")}`
  ));
}

function formatPatientName(value) {
  const cleaned = cleanText(value);
  if (!cleaned.includes(",")) return titleCase(cleaned);
  const [family, given] = cleaned.split(",", 2);
  return titleCase(`${given} ${family}`);
}

function isoDateFromSpanish(value) {
  const cleaned = cleanText(value);
  const isoMatch = cleaned.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2].padStart(2, "0")}-${isoMatch[3].padStart(2, "0")}`;
  }
  const match = cleaned.match(/(\d{1,2})\s+([A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+)\s+(\d{4})/);
  if (!match) return "";
  const month = spanishMonths[match[2].toLocaleLowerCase("es")];
  if (!month) return "";
  return `${match[3]}-${String(month).padStart(2, "0")}-${match[1].padStart(2, "0")}`;
}

function firstMatch(text, pattern, transform = (value) => cleanText(value)) {
  const match = text.match(pattern);
  return match ? transform(match[1]) : null;
}

function numbersAfter(text, labelPattern, count) {
  const pattern = new RegExp(`${labelPattern}\\s+(${NUMBER}(?:\\s+${NUMBER}){${count - 1}})`, "i");
  const match = text.match(pattern);
  if (!match) return null;
  const values = match[1].match(new RegExp(NUMBER, "g"))?.map(numeric) ?? [];
  return values.length === count ? values : null;
}

function numbersAfterOneOf(text, labelPattern, counts) {
  for (const count of counts) {
    const values = numbersAfter(text, labelPattern, count);
    if (values) return values;
  }
  return null;
}

function parseCompositionRow(text, labelPattern, format) {
  const count = format === "technical" ? 6 : 4;
  const values = numbersAfter(text, labelPattern, count);
  if (!values) return null;
  if (format === "technical") {
    const [bmcG, fatMassG, leanMassG, leanPlusBmcG, totalMassG, fatPercent] = values;
    return { bmcG, fatMassG, leanMassG, leanPlusBmcG, totalMassG, fatPercent };
  }
  const percentileValues = numbersAfterOneOf(text, labelPattern, [6, 5, 4]);
  const [fatMassG, leanPlusBmcG, totalMassG, fatPercent] = percentileValues;
  return {
    fatMassG,
    leanPlusBmcG,
    totalMassG,
    fatPercent,
    percentileYoung: percentileValues.length === 6 ? percentileValues[4] : null,
    percentileAgeMatched: percentileValues.length === 6
      ? percentileValues[5]
      : percentileValues[4] ?? null,
  };
}

function parseBmdRow(text, labelPattern) {
  const values = numbersAfterOneOf(text, labelPattern, [7, 5, 3]);
  if (!values) return null;
  const [areaCm2, bmcG, bmd] = values;
  if (values.length === 7) {
    const [, , , tScore, referenceMeanPercent, zScore, ageMatchedPercent] = values;
    return {
      areaCm2,
      bmcG,
      bmd,
      tScore,
      referenceMeanPercent,
      zScore,
      ageMatchedPercent,
    };
  }
  if (values.length === 5) {
    return {
      areaCm2,
      bmcG,
      bmd,
      tScore: null,
      referenceMeanPercent: null,
      zScore: values[3],
      ageMatchedPercent: values[4],
    };
  }
  return { areaCm2, bmcG, bmd };
}

function parseBilateralHipPage(text) {
  const neckMatch = text.match(
    /\bCuello\s*(?=Izquierdo\b)(.+?)(?=\bTotal\s*(?=Izquierdo\b))/i,
  );
  const totalMatch = text.match(
    /\bTotal\s*(?=Izquierdo\b)(.+?)(?=\d+(?:[.,]\d+)?\s*%\s*de\s*CV|Riesgo de fractura|Comentario:|$)/i,
  );
  if (!neckMatch || !totalMatch) return null;
  const sides = {
    left: {
      neck: parseBmdRow(neckMatch[1], "Izquierdo"),
      total: parseBmdRow(totalMatch[1], "Izquierdo"),
    },
    right: {
      neck: parseBmdRow(neckMatch[1], "Derecho"),
      total: parseBmdRow(totalMatch[1], "Derecho"),
    },
  };
  if (!sides.left.total && !sides.right.total) return null;
  return sides;
}

function selectHipSide(sides) {
  const candidates = Object.entries(sides ?? {})
    .map(([side, values]) => ({
      side,
      values,
      bmd: numeric(values?.total?.bmd) ?? numeric(values?.neck?.bmd),
    }))
    .filter(({ bmd }) => bmd !== null)
    .sort((left, right) => right.bmd - left.bmd);
  return candidates[0] ?? null;
}

function parseBodyComposition(pages, record, warnings) {
  const joined = pages.map((page) => page.text).join(" ");
  const summaryPage = [...pages].reverse().find((page) => (
    /Resumen de resultados DEXA/i.test(page.text)
    && /Brazo\s+I/i.test(page.text)
    && /% de grasa/i.test(page.text)
    && Boolean(parseCompositionRow(page.text, "\\bTotal\\b", "technical"))
  ));
  const bodyPage = pages.find((page) => (
    /Resultados de composición corporal/i.test(page.text)
    && /Índices adiposos/i.test(page.text)
  ));
  const fullText = summaryPage?.text ?? "";
  const bodyText = bodyPage?.text ?? joined;
  const regionLabels = {
    leftArm: "Brazo\\s+I",
    rightArm: "Brazo\\s+D",
    trunk: "Tronco",
    leftLeg: "Pierna\\s+I",
    rightLeg: "Pierna\\s+D",
    subtotal: "\\bSubtotal\\b",
    head: "Cabeza",
    total: "\\bTotal\\b",
  };
  for (const [key, label] of Object.entries(regionLabels)) {
    const parsed = fullText ? parseCompositionRow(fullText, label, "technical") : null;
    const fallback = parseCompositionRow(bodyText, label, "body");
    const value = parsed || fallback
      ? { ...(fallback ?? {}), ...(parsed ?? {}) }
      : null;
    if (!value) continue;
    if (key === "total" || key === "subtotal") record.composition[key] = {
      ...record.composition[key],
      ...value,
    };
    else record.composition.regions[key] = { ...record.composition.regions[key], ...value };
  }

  const android = parseCompositionRow(bodyText, "Androide\\s*\\(A\\)", "body");
  const gynoid = parseCompositionRow(bodyText, "Ginoide\\s*\\(G\\)", "body");
  if (android) record.composition.regions.android = {
    ...record.composition.regions.android,
    ...android,
  };
  if (gynoid) record.composition.regions.gynoid = {
    ...record.composition.regions.gynoid,
    ...gynoid,
  };

  const indices = record.composition.indices;
  indices.bmi = firstMatch(bodyText, /BMI\s*=\s*([0-9.,]+)/i, numeric);
  const bodyFat = numbersAfterOneOf(
    bodyText,
    "%\\s*de\\s*grasa\\s*corporal\\s*total",
    [3, 2, 1],
  );
  if (bodyFat) {
    record.composition.total.fatPercent ??= bodyFat[0];
    indices.bodyFatPercentileYoung = bodyFat.length === 3 ? bodyFat[1] : null;
    indices.bodyFatPercentileAgeMatched = bodyFat.length === 3 ? bodyFat[2] : bodyFat[1] ?? null;
    record.composition.total.percentileYoung = indices.bodyFatPercentileYoung;
    record.composition.total.percentileAgeMatched = indices.bodyFatPercentileAgeMatched;
  }
  const fmi = numbersAfterOneOf(
    bodyText,
    "Masa\\s*grasa\\/altura[²2]?\\s*\\(kg\\/m[²2]\\)",
    [3, 2, 1],
  );
  if (fmi) {
    indices.fmi = fmi[0];
    indices.fmiPercentileYoung = fmi.length === 3 ? fmi[1] : null;
    indices.fmiPercentileAgeMatched = fmi.length === 3 ? fmi[2] : fmi[1] ?? null;
  }
  indices.androidGynoidRatio = firstMatch(
    bodyText,
    /Proporción\s+androide\/ginoide\s+(-?\d+(?:[.,]\d+)?)/i,
    numeric,
  );
  const trunkLeg = numbersAfter(bodyText, "%\\s*gras\\.\\s*en\\s*tron\\.\\/%\\s*gras\\.\\s*en\\s*pier\\.", 3);
  if (trunkLeg) {
    indices.trunkLegFatRatio = trunkLeg[0];
    indices.trunkLegFatPercentileYoung = trunkLeg[1];
    indices.trunkLegFatPercentileAgeMatched = trunkLeg[2];
  }
  const trunkExtremity = numbersAfter(bodyText, "Prop\\.\\s*de\\s*masa\\s*grasa\\s*de\\s*tron\\.\\/extr\\.", 3);
  if (trunkExtremity) {
    indices.trunkExtremityFatRatio = trunkExtremity[0];
    indices.trunkExtremityFatPercentileYoung = trunkExtremity[1];
    indices.trunkExtremityFatPercentileAgeMatched = trunkExtremity[2];
  }
  indices.vatMassG = firstMatch(bodyText, /Est\.\s*VAT\s*Mass\s*\(g\)\s*([0-9.,]+)/i, numeric);
  indices.vatVolumeCm3 = firstMatch(bodyText, /Est\.\s*VAT\s*Volume\s*\(cm[³3]\)\s*([0-9.,]+)/i, numeric);
  indices.vatAreaCm2 = firstMatch(bodyText, /Est\.\s*VAT\s*Area\s*\(cm[²2]\)\s*([0-9.,]+)/i, numeric);
  const lmi = numbersAfterOneOf(
    bodyText,
    "Lean\\/Height[²2]\\s*\\(kg\\/m[²2]\\)",
    [3, 2, 1],
  );
  if (lmi) {
    indices.lmi = lmi[0];
    indices.lmiPercentileYoung = lmi.length === 3 ? lmi[1] : null;
    indices.lmiPercentileAgeMatched = lmi.length === 3 ? lmi[2] : lmi[1] ?? null;
  }
  const almi = numbersAfterOneOf(
    bodyText,
    "Appen\\.\\s*Lean\\/Height[²2]\\s*\\(kg\\/m[²2]\\)",
    [3, 2, 1],
  );
  if (almi) {
    indices.almi = almi[0];
    indices.almiPercentileYoung = almi.length === 3 ? almi[1] : null;
    indices.almiPercentileAgeMatched = almi.length === 3 ? almi[2] : almi[1] ?? null;
  }
  if (!bodyPage) warnings.push("No se identificó con certeza la página de composición corporal.");
}

function parseBone(pages, record, warnings) {
  const wholeBodyPage = pages.find((page) => (
    /Resumen de resultados DEXA/i.test(page.text)
    && /Brazo\s+I/i.test(page.text)
    && /Cost\.\s*I/i.test(page.text)
    && /Colum\.\s*D/i.test(page.text)
    && /DMO/i.test(page.text)
  ));
  if (wholeBodyPage) {
    const labels = {
      leftArm: "Brazo\\s+I",
      rightArm: "Brazo\\s+D",
      leftRibs: "Cost\\.\\s*I",
      rightRibs: "Cost\\.\\s*D",
      thoracicSpine: "Colum\\.\\s*D",
      lumbarSpine: "Colum\\.\\s*L",
      pelvis: "Pelvis",
      leftLeg: "Pierna\\s+I",
      rightLeg: "Pierna\\s+D",
      head: "Cabeza",
    };
    for (const [key, label] of Object.entries(labels)) {
      const values = numbersAfter(wholeBodyPage.text, label, 3);
      if (values) {
        const [areaCm2, bmcG, bmd] = values;
        record.bone.wholeBodyRegions[key] = {
          ...record.bone.wholeBodyRegions[key],
          areaCm2,
          bmcG,
          bmd,
        };
      }
    }
    const total = parseBmdRow(wholeBodyPage.text, "\\bTotal\\b");
    if (total) {
      record.bone.wholeBody = {
        ...record.bone.wholeBody,
        ...total,
      };
    }
  } else {
    warnings.push("No se identificó con certeza la tabla de DMO de cuerpo entero.");
  }

  const bilateralHipPage = pages.find((page) => (
    /\bCuello\s*Izquierdo/i.test(page.text)
    && /\bCuello[\s\S]+?Derecho/i.test(page.text)
    && /\bTotal\s*Izquierdo/i.test(page.text)
  ));
  const bilateralHip = bilateralHipPage
    ? parseBilateralHipPage(bilateralHipPage.text)
    : null;
  const selectedHip = selectHipSide(bilateralHip);
  const hipPage = bilateralHipPage
    ?? pages.find((page) => /Cadera\s+izquierda|Cadera\s+derecha/i.test(page.text));
  if (hipPage) {
    if (selectedHip) {
      record.bone.hip.sides = {
        left: {
          neck: { ...record.bone.hip.sides.left.neck, ...(bilateralHip.left.neck ?? {}) },
          total: { ...record.bone.hip.sides.left.total, ...(bilateralHip.left.total ?? {}) },
        },
        right: {
          neck: { ...record.bone.hip.sides.right.neck, ...(bilateralHip.right.neck ?? {}) },
          total: { ...record.bone.hip.sides.right.total, ...(bilateralHip.right.total ?? {}) },
        },
      };
      record.bone.hip.side = selectedHip.side;
      record.bone.hip.neck = {
        ...record.bone.hip.neck,
        ...(selectedHip.values.neck ?? {}),
      };
      record.bone.hip.total = {
        ...record.bone.hip.total,
        ...(selectedHip.values.total ?? {}),
      };
    } else {
      record.bone.hip.side = /Cadera\s+derecha/i.test(hipPage.text) ? "right" : "left";
      record.bone.hip.neck = { ...record.bone.hip.neck, ...parseBmdRow(hipPage.text, "Cuello") };
      const hipTotal = parseBmdRow(hipPage.text, "\\bTotal\\b");
      if (hipTotal) {
        record.bone.hip.total = {
          ...record.bone.hip.total,
          ...hipTotal,
        };
      }
    }
    record.bone.hip.fraxReported = /FRAX[^:]*:\s*(?!No se informó)/i.test(hipPage.text);
    record.bone.hip.fraxReason = firstMatch(
      hipPage.text,
      /No se informó FRAX porque:\s*(.+?)(?:Fractura vertebral|Comentario:|Información de la exploración:|$)/i,
    ) ?? "";
  } else {
    warnings.push("No se identificó una página de cadera.");
  }

  const lumbarPage = pages.find((page) => (
    /Columna\s+vertebral\s+lumbar/i.test(page.text)
    && /L1\s+/.test(page.text)
    && /L4\s+/.test(page.text)
  ));
  if (lumbarPage) {
    for (const key of ["l1", "l2", "l3", "l4"]) {
      record.bone.lumbar[key] = {
        ...record.bone.lumbar[key],
        ...parseBmdRow(lumbarPage.text, key.toLocaleUpperCase("es")),
      };
    }
    const lumbarTotal = parseBmdRow(
      lumbarPage.text,
      "(?:L1\\s*-\\s*L4|\\bTotal\\b)",
    );
    if (lumbarTotal) {
      record.bone.lumbar.total = {
        ...record.bone.lumbar.total,
        ...lumbarTotal,
      };
    }
  } else {
    warnings.push("No se identificó una página de columna lumbar.");
  }
  const scmoPage = pages.find((page) => /sCMO/i.test(page.text));
  if (scmoPage) {
    record.bone.lumbar.scmoMgCm2 = firstMatch(
      scmoPage.text,
      new RegExp(`Total\\s+(${NUMBER})`, "i"),
      numeric,
    );
  }
}

function countDetectedValues(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (Array.isArray(value)) return value.reduce((sum, item) => sum + countDetectedValues(item), 0);
  if (typeof value !== "object") return 1;
  return Object.entries(value)
    .filter(([key]) => !["source", "professional", "schemaVersion"].includes(key))
    .reduce((sum, [, item]) => sum + countDetectedValues(item), 0);
}

export function parseDexaPages(pages, { fileName = "" } = {}) {
  const record = createEmptyRecord();
  const warnings = [];
  const normalizedPages = pages.map((page, index) => ({
    pageNumber: page.pageNumber ?? index + 1,
    text: cleanText(page.text ?? page.lines?.join(" ") ?? ""),
    imageUrl: page.imageUrl ?? "",
    role: page.role ?? detectDexaPageRole(page.text ?? page.lines?.join(" ") ?? ""),
    visuals: page.visuals ?? {},
  }));
  const allText = normalizedPages.map((page) => page.text).join(" ");

  record.patient.name = formatPatientName(
    firstMatch(allText, /Nombre:\s*(.+?)(?=Edad:|Fecha de nacimiento:|Sexo:)/i) ?? "",
  );
  record.patient.age = firstMatch(allText, /Edad:\s*(\d{1,3})/i, numeric);
  record.patient.birthDate = isoDateFromSpanish(
    firstMatch(allText, /Fecha de nacimiento:\s*(.+?)(?=Sexo:|Raza:|Height:|Peso:)/i) ?? "",
  );
  const sex = firstMatch(allText, /Sexo:\s*(Mujer|Hombre|Femenino|Masculino)/i);
  record.patient.sex = /mujer|femenino/i.test(sex ?? "")
    ? "female"
    : /hombre|masculino/i.test(sex ?? "")
      ? "male"
      : "";
  record.patient.ethnicity = firstMatch(allText, /Raza:\s*(.+?)(?=Height:|Peso:|ID del paciente:)/i) ?? "";
  record.patient.heightCm = firstMatch(allText, /Height:\s*([0-9.,]+)\s*cm/i, numeric);
  record.patient.scaleWeightKg = firstMatch(allText, /Peso:\s*([0-9.,]+)\s*kg/i, numeric);
  record.patient.patientId = firstMatch(allText, /ID del paciente:\s*([A-Za-z0-9-]+)/i) ?? "";
  record.exam.date = isoDateFromSpanish(
    firstMatch(allText, /Fecha exploración:\s*(.+?)(?=ID:|Tipo exploración:|Análisis:)/i) ?? "",
  );
  record.exam.examId = firstMatch(allText, /Fecha exploración:.*?ID:\s*([A-Za-z0-9-]+)/i) ?? "";
  record.exam.vendor = /HOLOGIC/i.test(allText) ? "Hologic" : "";
  record.exam.model = firstMatch(
    allText,
    /Modelo:\s*(.+?)(?=Est\.\s*VAT|Comentario:|Im[aá]genes?\s+no\s+v[aá]lidas?|$)/i,
  ) ?? "";
  record.exam.analysisVersion = firstMatch(allText, /Versión\s*([0-9.]+)/i) ?? "";
  record.exam.referrer = firstMatch(allText, /Médico remitente:\s*(.+?)(?=Información de la exploración:|Resumen de resultados|$)/i) ?? "";
  record.exam.sourceFileName = fileName;

  parseBodyComposition(normalizedPages, record, warnings);
  parseBone(normalizedPages, record, warnings);

  const detectedFields = countDetectedValues(record);
  const expectedCoreFields = 70;
  record.source = {
    pageCount: normalizedPages.length,
    extractedAt: new Date().toISOString(),
    parser: record.exam.vendor === "Hologic" ? "hologic-es-v2" : "generic-dxa-v1",
    confidence: Math.min(1, detectedFields / expectedCoreFields),
    detectedFields,
    warnings,
    pages: normalizedPages.map(({
      pageNumber,
      imageUrl,
      role,
      visuals,
    }) => ({
      pageNumber,
      imageUrl,
      role,
      visuals,
    })),
  };
  return record;
}

export const parserInternals = {
  cleanText,
  isoDateFromSpanish,
  numeric,
  numbersAfter,
  parseBilateralHipPage,
  selectHipSide,
};
